if (!customElements.get('pdp-aov-boaster')) {
  customElements.define('pdp-aov-boaster', class PdpAovBoaster extends HTMLElement {
    connectedCallback() {
      if (this.dataset.aovObserved === 'true') return;
      this.dataset.aovObserved = 'true';

      this.isActive = false;
      this.observer = null;

      if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
        this.activate();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.activate();
        this.observer?.disconnect();
        this.observer = null;
      }, {
        rootMargin: '350px 0px',
        threshold: 0.01,
      });

      this.observer.observe(this);
    }

    activate() {
      if (this.isActive) return;
      this.isActive = true;
      this.dataset.aovReady = 'true';

      this.items = Array.from(this.querySelectorAll('[data-addon]'));
      this.summary = this.querySelector('[data-summary-copy]');
      this.threshold = this.querySelector('[data-threshold-copy]');
      this.progress = this.querySelector('[data-progress]');
      this.error = this.querySelector('[data-aov-error]');
      this.copy = JSON.parse(this.querySelector('[data-aov-copy]')?.textContent || '{}');
      this.basePrice = Number(this.dataset.basePrice || 0);
      this.deliveryThreshold = Number(this.dataset.deliveryThreshold || 0);
      this.giftThreshold = Number(this.dataset.giftThreshold || 0);
      this.currency = this.dataset.currency || 'GBP';
      this.selected = new Map();

      this.onItemClick = this.onItemClick.bind(this);
      this.onDocumentChange = this.onDocumentChange.bind(this);
      this.onDocumentSubmit = this.onDocumentSubmit.bind(this);

      this.items.forEach((item) => item.addEventListener('click', this.onItemClick));
      document.addEventListener('change', this.onDocumentChange);
      document.addEventListener('submit', this.onDocumentSubmit, true);

      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;

      if (!this.isActive) return;
      this.items?.forEach((item) => item.removeEventListener('click', this.onItemClick));
      document.removeEventListener('change', this.onDocumentChange);
      document.removeEventListener('submit', this.onDocumentSubmit, true);
    }

    money(cents) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(cents / 100);
    }

    onItemClick(event) {
      const item = event.currentTarget;
      if (item.disabled) return;

      const id = Number(item.dataset.addonId);
      if (!id) return;

      if (this.selected.has(id)) {
        this.selected.delete(id);
      } else {
        this.selected.set(id, {
          id,
          quantity: 1,
          price: Number(item.dataset.addonPrice || 0),
          name: item.dataset.addonName || '',
        });
      }

      const isSelected = this.selected.has(id);
      item.classList.toggle('is-selected', isSelected);
      item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      this.render();
    }

    onDocumentChange(event) {
      const variantElement = event.target.closest('variant-radios, variant-selects');
      if (!variantElement) return;

      requestAnimationFrame(() => {
        if (variantElement.currentVariant?.price != null) {
          this.basePrice = Number(variantElement.currentVariant.price);
          this.render();
        }
      });
    }

    getTotals() {
      const selectedAddons = Array.from(this.selected.values());
      const addonsTotal = selectedAddons.reduce((total, addon) => total + addon.price, 0);
      return {
        selectedAddons,
        selectedCount: selectedAddons.length,
        addonsTotal,
        basketTotal: this.basePrice + addonsTotal,
      };
    }

    render() {
      const totals = this.getTotals();
      const deliveryThreshold = Math.max(this.deliveryThreshold, 1);
      const giftThreshold = Math.max(this.giftThreshold, deliveryThreshold);
      const nextThreshold = totals.basketTotal < deliveryThreshold ? deliveryThreshold : giftThreshold;
      const percentage = Math.min((totals.basketTotal / nextThreshold) * 100, 100);

      if (this.summary) {
        this.summary.innerHTML = totals.selectedCount === 0
          ? this.copy.empty
          : `<strong>${totals.selectedCount} ${this.copy.selected}</strong> · ${this.copy.basketLabel} <strong>${this.money(totals.basketTotal)}</strong>`;
      }

      if (this.threshold) {
        if (totals.basketTotal >= giftThreshold) {
          this.threshold.textContent = this.copy.allUnlocked;
        } else if (totals.basketTotal >= deliveryThreshold) {
          this.threshold.textContent = `${this.copy.deliveryUnlocked} · ${this.money(giftThreshold - totals.basketTotal)} ${this.copy.giftAway}`;
        } else {
          this.threshold.textContent = `${this.money(deliveryThreshold - totals.basketTotal)} ${this.copy.deliveryAway}`;
        }
      }

      if (this.progress) this.progress.style.width = `${percentage}%`;

      window.dispatchEvent(new CustomEvent('cardology:aov-addons-change', {
        detail: {
          selectedAddons: totals.selectedAddons,
          selectedIds: totals.selectedAddons.map((addon) => addon.id),
          addonsTotal: totals.addonsTotal,
          basketTotal: totals.basketTotal,
        },
      }));
    }

    buildItems(mainId, mainQuantity) {
      const quantities = new Map();
      quantities.set(mainId, mainQuantity);

      this.selected.forEach((addon) => {
        quantities.set(addon.id, (quantities.get(addon.id) || 0) + addon.quantity);
      });

      return Array.from(quantities.entries()).map(([id, quantity]) => ({ id, quantity }));
    }

    setLoading(form, isLoading) {
      const buttons = form.querySelectorAll('[type="submit"], [data-add-to-cart]');
      buttons.forEach((button) => {
        button.disabled = isLoading;
        button.toggleAttribute('aria-disabled', isLoading);
        button.classList.toggle('btn--loading', isLoading);
        button.classList.toggle('product-form__cart-submit--loading', isLoading);
      });

      const stickySubmit = document.querySelector('pdp-sticky-add-to-cart [data-sticky-submit]');
      if (stickySubmit) {
        stickySubmit.disabled = isLoading;
        stickySubmit.toggleAttribute('aria-disabled', isLoading);
      }
    }

    showError(message = '') {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    waitForPersonalizationCart(timeout = 5000) {
      if (window.OECartDrawer?.open) return Promise.resolve(true);

      return new Promise((resolve) => {
        const started = Date.now();
        const check = () => {
          if (window.OECartDrawer?.open) {
            resolve(true);
            return;
          }

          if (Date.now() - started >= timeout) {
            resolve(false);
            return;
          }

          requestAnimationFrame(check);
        };

        check();
      });
    }

    async buildPersonalizationAwareItems(form, mainId, mainQuantity) {
      form.dataset.oeCartDrawerSubmitting = '1';

      if (typeof form.oePreparePersonalization === 'function') {
        await form.oePreparePersonalization(form);
      } else if (typeof window.oePreparePersonalization === 'function') {
        await window.oePreparePersonalization(form);
      }

      let items = null;

      if (typeof form.oeBuildPersonalizationItems === 'function') {
        items = form.oeBuildPersonalizationItems(form);
      } else if (typeof window.oeBuildPersonalizationItems === 'function') {
        items = window.oeBuildPersonalizationItems(form);
      }

      if (!Array.isArray(items) || !items.length) {
        items = [{ id: mainId, quantity: mainQuantity }];
      } else {
        items = items.map((item) => ({ ...item }));
      }

      this.selected.forEach((addon) => {
        const existingPlainItem = items.find((item) => (
          Number(item.id) === addon.id &&
          !item.parent_id &&
          (!item.properties || Object.keys(item.properties).length === 0)
        ));

        if (existingPlainItem) {
          existingPlainItem.quantity = Number(existingPlainItem.quantity || 1) + addon.quantity;
        } else {
          items.push({ id: addon.id, quantity: addon.quantity });
        }
      });

      return items;
    }

    async onDocumentSubmit(event) {
      if (!this.selected.size || event.__cardologyAovHandled) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('.pdp-main-product')) return;

      const formData = new FormData(form);
      const mainId = Number(formData.get('id'));
      const mainQuantity = Number(formData.get('quantity') || 1);
      if (!mainId) return;

      event.__cardologyAovHandled = true;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError();
      this.setLoading(form, true);

      try {
        const drawerReady = await this.waitForPersonalizationCart();
        if (!drawerReady) {
          throw new Error('Personalization cart is not ready. Please try again.');
        }

        const items = await this.buildPersonalizationAwareItems(form, mainId, mainQuantity);
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        });

        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || this.copy.cartError);
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'pdp-aov-boaster',
            productVariantId: mainId,
          });
        }

        await window.OECartDrawer.open();
      } catch (error) {
        console.error('[PDP AOV Boaster]', error);
        this.showError(error.message || this.copy.cartError);
      } finally {
        form.dataset.oeCartDrawerSubmitting = '0';
        this.setLoading(form, false);
      }
    }
  });
}